import React from 'react';
import { ChevronDown } from 'lucide-react';
import { billingRateLabel, formatBillingDuration, formatBillingMoney, formatBillingQuantity, statementTotalSummary } from '../../utils/billingCalculation.js';
import { CLIENT_INFORMATION_UNAVAILABLE, clientServiceDateLabel } from '../../utils/billingClientExplanation.js';
import { approx, commonShare, continuesFrom, estimatedLabel, excludedSeconds, kmLabel, STOP_KINDS, stopDeparturePlace, summarizeBillableTime, timeLabel, tonsLabel, workedDayCount } from '../../utils/billingCalculationDays.js';
import BillingServiceRoute from './BillingServiceRoute.jsx';

const quantityLabel = (quantity, unit) => quantity === null || !unit ? CLIENT_INFORMATION_UNAVAILABLE : formatBillingQuantity(quantity, unit);
const durationLabel = (seconds) => seconds === null ? CLIENT_INFORMATION_UNAVAILABLE : formatBillingDuration(seconds);
const rateLabel = (rate, unit, currency) => rate === null || !unit ? 'Tarif à déterminer' : billingRateLabel(rate, unit, currency);

function Period({ model }) {
  if (!model.firstDate) return model.freeLines.length ? null : 'Dates des prestations non disponibles';
  if (model.firstDate === model.lastDate) return <>{clientServiceDateLabel(model.firstDate)}</>;
  return <>Du {clientServiceDateLabel(model.firstDate)} au {clientServiceDateLabel(model.lastDate)}</>;
}

function MoneyTotals({ totals }) {
  return totals.map((total) => <span key={total.currencyCode} className="billing-client-explanation-money">
    {formatBillingMoney(total.amount, total.currencyCode)}
  </span>);
}

function PrestationDetail({ detail }) {
  return <article className="billing-client-explanation-prestation">
    <div className="billing-client-explanation-prestation-row">
      <div className="billing-client-explanation-reference">
        <h4>{detail.customerName || (detail.reference ? 'Client non disponible' : 'Prestation commune')}</h4>
        <p>{detail.reference ? `Commande ${detail.reference} · ` : ''}{detail.dateLabel}</p>
      </div>
      <div data-label="Quantité ou durée">{quantityLabel(detail.quantity, detail.unit)}</div>
      <div data-label="Tarif">{rateLabel(detail.rate, detail.unit, detail.currency)}</div>
      <strong data-label="Montant">{formatBillingMoney(detail.amount, detail.currency)}</strong>
    </div>
    <details className="billing-client-explanation-calculation">
      <summary><span>Calcul<span className="sr-only"> · {detail.customerName} · {detail.reference || detail.dateLabel}</span></span><ChevronDown size={14} aria-hidden="true" /></summary>
      <div className="billing-client-explanation-calculation-body">
        {detail.hasBlock ? <BillingServiceRoute context={detail.serviceContext} /> : null}
        <p className="billing-client-explanation-equation" aria-label="Calcul de la prestation">
          <span>{quantityLabel(detail.quantity, detail.unit)}</span><span aria-hidden="true">×</span>
          <span>{rateLabel(detail.rate, detail.unit, detail.currency)}</span>
          <span aria-hidden="true">→</span><strong>{formatBillingMoney(detail.amount, detail.currency)}</strong>
        </p>
        {detail.hourly ? <>
          <p>Durées relevées avant répartition ; la quantité ci-dessus est la part attribuée à cette prestation.</p>
          <dl className="billing-client-explanation-facts">
            {detail.hasBlock ? <>
              <div><dt>Trajet</dt><dd>{durationLabel(detail.travelSeconds)}</dd></div>
              <div><dt>Sur place</dt><dd>{durationLabel(detail.onsiteSeconds)}</dd></div>
            </> : null}
            <div><dt>{detail.hasBlock ? 'Durée après exclusions' : 'Durée avant répartition'}</dt><dd>{durationLabel(detail.netSeconds)}</dd></div>
            <div><dt>Temps exclu</dt><dd>{durationLabel(detail.excludedSeconds)}</dd></div>
          </dl>
          <p>{detail.allocation || `Détail de la répartition : ${CLIENT_INFORMATION_UNAVAILABLE.toLowerCase()}.`}</p>
        </> : null}
        {detail.exceptional ? <p>Un tarif particulier est enregistré pour cette commande.</p> : null}
        {!detail.hasEvidence ? <p>Le détail du parcours n’est pas disponible pour cette prestation.</p> : null}
      </div>
    </details>
  </article>;
}

function CategorySummaryRow({ category, expanded, onToggle }) {
  const detailId = React.useId();
  const rate = category.rateState === 'missing' ? 'Tarif à déterminer' : category.rateState === 'variable'
    ? 'Tarifs variables' : rateLabel(category.rate.value, category.rate.unit, category.rate.currency);
  return <>
    <tr className={`billing-client-explanation-category${expanded ? ' is-open' : ''}`} onClick={onToggle}>
      <th scope="row" className="billing-client-explanation-category-name">
        <span>{category.label}</span><p>{category.description}</p>
      </th>
      <td className="billing-client-explanation-quantity" data-label="Quantité ou durée">
        {category.quantities.map(({ unit, quantity }) => <span key={unit}>{quantityLabel(quantity, unit)}</span>)}
      </td>
      <td className="billing-client-explanation-rate" data-label="Tarif">{rate}</td>
      <td className="billing-client-explanation-amount" data-label="Montant"><MoneyTotals totals={category.totals} /></td>
      <td className="billing-client-explanation-action">
        <button type="button" aria-expanded={expanded} aria-controls={detailId}
          aria-label={`${expanded ? 'Masquer' : 'Voir'} le détail · ${category.label}`}
          onClick={(event) => { event.stopPropagation(); onToggle(); }}>
          <span>{expanded ? 'Masquer le détail' : 'Voir le détail'}</span><ChevronDown size={14} aria-hidden="true" />
        </button>
      </td>
    </tr>
    <tr id={detailId} hidden={!expanded} className="billing-client-explanation-category-detail">
      <td colSpan={5}>
        {expanded ? <section aria-label={`Prestations · ${category.label}`}>
          {category.posts.map((post) => <div key={post.key}>
            {category.posts.length > 1 ? <p className="billing-client-explanation-rate-group">
              {rateLabel(post.rate, post.unit, post.currency)}{post.side ? ` · ${post.side === 'loading' ? 'Chargement' : 'Déchargement'}` : ''}
            </p> : null}
            {post.details.map((detail) => <PrestationDetail key={detail.key} detail={detail} />)}
          </div>)}
        </section> : null}
      </td>
    </tr>
  </>;
}

const clock = (value, zone) => value ? new Intl.DateTimeFormat('fr-CA', { hour: 'numeric', minute: '2-digit', timeZone: zone || 'America/Toronto' }).format(new Date(value)) : '';
const plural = (count, word) => `${count} ${word}${count > 1 ? 's' : ''}`;
const joined = (...parts) => parts.filter(Boolean).join(' · ');
const hoursLabel = (hours) => hours === undefined ? '' : timeLabel(hours === null ? null : hours * 3600);
const moneyLabel = (totals) => totals.map((total) => formatBillingMoney(total.amount, total.currencyCode)).join(' · ');
const onsite = (time) => `${approx(time.loading.estimated || time.delivery.estimated)}${timeLabel(time.loading.seconds + time.delivery.seconds)}`;
const road = (time, digits = 1) => joined(`${approx(time.route.estimated)}${timeLabel(time.route.seconds)}`, kmLabel(time.route.km, digits));

function Box({ label, value, note, strong = false }) {
  return <div className={`billing-client-explanation-box${strong ? ' is-result' : ''}`}>
    <span>{label}</span><strong>{value}</strong>{note ? <small>{note}</small> : null}
  </div>;
}

function HoursSummary({ time }) {
  const { loading, route, delivery } = time;
  const boxes = [
    loading.count && { label: 'Chargement en meunerie', value: timeLabel(loading.seconds), note: joined(plural(loading.count, 'chargement'), tonsLabel(loading.tons || null), estimatedLabel(loading.estimated)) },
    route.count && { label: 'Route', value: timeLabel(route.seconds), note: joined(plural(route.count, 'trajet'), kmLabel(route.km), estimatedLabel(route.estimated)) },
    delivery.count && { label: 'Livraison sur place', value: timeLabel(delivery.seconds), note: joined(plural(delivery.count, 'livraison'), tonsLabel(delivery.tons || null), estimatedLabel(delivery.estimated)) },
    time.inspection && { label: 'Inspection', value: timeLabel(time.inspection), note: 'votre part' },
    time.back && { label: 'Retour', value: timeLabel(time.back), note: 'votre part' },
    time.other && { label: 'Autre temps', value: timeLabel(time.other), note: 'sans détail du trajet' },
  ].filter(Boolean);
  const rate = time.rate ? `à ${billingRateLabel(time.rate.rate, time.rate.unit, time.rate.currency)}` : time.rateMissing ? 'tarif à déterminer' : 'plusieurs tarifs';
  return <section aria-labelledby="client-explanation-summary" className="billing-client-explanation-summary">
    <h2 id="client-explanation-summary">Comment vos heures sont calculées</h2>
    <div className="billing-client-explanation-boxes" aria-label="Calcul des heures facturées">
      {boxes.map((box, index) => <React.Fragment key={box.label}>{index ? <span aria-hidden="true">+</span> : null}<Box {...box} /></React.Fragment>)}
      <span aria-hidden="true">=</span>
      <Box strong label="Heures facturées" value={hoursLabel(time.hours)} note={`${rate} · ${moneyLabel(time.hourTotals)}`} />
    </div>
  </section>;
}

function stopLabel(step) {
  const kind = <><span className="billing-client-explanation-stop-kind">{STOP_KINDS[step.kind]}</span>{' '}</>;
  if (step.kind === 'preload') return <>{kind}{tonsLabel(step.tons)}</>;
  const detail = step.kind === 'loading' ? '' : joined(step.orders.length ? `${step.orders.length} cmd` : '', tonsLabel(step.tons));
  return <>{kind}<strong className="billing-client-explanation-stop-name">{step.site}</strong>{detail ? ` · ${detail}` : ''}</>;
}

// « Comptoir Agricole à 13 h 51 », « après une livraison · autre client à 11 h 49 » ou seulement l'heure.
function departureText(step, timezone) {
  if (!step || step.kind === 'preload') return '';
  const start = clock(step.block?.start, timezone);
  return [stopDeparturePlace(step), start ? `à ${start}` : ''].filter(Boolean).join(' ');
}

const routeLabel = (seconds, km, estimated) => <>{approx(estimated)}{timeLabel(seconds)}{km === null ? null : <span className="billing-client-explanation-km"> · {kmLabel(km, 1)}</span>}</>;

function DayTable({ day, title }) {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  const time = summarizeBillableTime(day.items);
  const stops = day.steps.filter((step) => ['loading', 'delivery', 'dompeur', 'preload'].includes(step.kind));
  const share = commonShare(day);
  // Le premier départ est dans le titre de la journée ; les suivants seulement s'ils ne reprennent pas l'arrêt du dessus.
  const start = departureText(stops[0], day.timezone);
  return <div className={`billing-client-explanation-day-group${open ? ' is-open' : ''}`}>
    <button type="button" className="billing-client-explanation-day-toggle" aria-expanded={open} aria-controls={id} onClick={() => setOpen(!open)}>
      <ChevronDown size={14} aria-hidden="true" /><strong>{title}</strong>{start ? <small>départ : {start}</small> : null}
      <span>{hoursLabel(time.hours)} · {moneyLabel(day.totals)}</span>
    </button>
    <table id={id} className="billing-client-explanation-day" aria-label={title}>
    <thead><tr><th scope="col">Arrêt</th><th scope="col">Route</th><th scope="col">Arrivée</th><th scope="col">Sur place</th><th scope="col">Départ</th><th scope="col">Heures</th></tr></thead>
    <tbody>
      {stops.map((step, index) => <React.Fragment key={step.key}>
        {index > 0 && departureText(step, day.timezone) && !continuesFrom(step, stops[index - 1], stopDeparturePlace(step))
          ? <tr className="billing-client-explanation-departure-row"><td colSpan={6}><span className="billing-client-explanation-departure">Départ : {departureText(step, day.timezone)}</span></td></tr> : null}
        <tr data-stop={step.kind}>
        <th scope="row">{stopLabel(step)}{excludedSeconds(step.block) ? <span className="billing-client-explanation-excluded"> · {timeLabel(excludedSeconds(step.block))} exclues</span> : null}</th>
        <td>{step.travel === null ? '' : routeLabel(step.travel, step.km, step.estimated)}</td>
        <td>{clock(step.block?.arrival, day.timezone)}</td>
        <td>{step.onsite === null ? '' : `${approx(step.estimated)}${timeLabel(step.onsite)}`}</td>
        <td>{clock(step.block?.end, day.timezone)}</td>
        <td>{step.seconds === null ? formatBillingQuantity(step.items[0].quantity, step.items[0].billing_unit) : timeLabel(step.seconds)}</td>
        </tr>
      </React.Fragment>)}
      {time.inspection || time.back ? <tr className="is-common" data-stop="common"><th scope="row"><span className="billing-client-explanation-stop-kind">Inspection et retour</span>{' '}{share != null && share < 0.9995 ? `votre part ${Math.round(share * 100)} %` : ''}</th>
        <td /><td /><td /><td /><td>{timeLabel(time.inspection + time.back)}</td></tr> : null}
    </tbody>
    <tfoot><tr><th scope="row">Total de la journée · {moneyLabel(day.totals)}</th><td>{routeLabel(time.route.seconds, time.route.km, time.route.estimated)}</td><td /><td>{onsite(time)}</td><td /><td>{hoursLabel(time.hours)}</td></tr></tfoot>
    </table>
  </div>;
}

function dayTitles(days) {
  const seen = new Map();
  return new Map(days.map((day) => {
    const index = (seen.get(day.date) || 0) + 1;
    seen.set(day.date, index);
    const date = day.date ? new Intl.DateTimeFormat('fr-CA', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${day.date}T12:00:00Z`)) : 'Date non disponible';
    const many = days.filter((other) => other.date === day.date).length > 1;
    return [day.key, `${date.charAt(0).toUpperCase()}${date.slice(1)}${many ? ` · tournée ${index}` : ''}`];
  }));
}

function DriverRows({ driver, titles }) {
  const [open, setOpen] = React.useState(false);
  const id = React.useId();
  const { summary } = driver;
  const name = joined(driver.driver || 'Chauffeur non indiqué', ...driver.trucks);
  return <>
    <tr className={`billing-client-explanation-driver${open ? ' is-open' : ''}`} onClick={() => setOpen(!open)}>
      <th scope="row"><button type="button" aria-expanded={open} aria-controls={id} onClick={(event) => { event.stopPropagation(); setOpen(!open); }}>
        <ChevronDown size={14} aria-hidden="true" />{name}</button></th>
      <td>{workedDayCount(driver.days)}</td><td>{road(summary, 0)}</td><td>{onsite(summary)}</td>
      <td>{timeLabel(summary.inspection + summary.back)}</td><td>{hoursLabel(summary.hours)}</td><td>{moneyLabel(summary.totals)}</td>
    </tr>
    <tr id={id} hidden={!open} className="billing-client-explanation-driver-days"><td colSpan={7}>
      {open ? <div>{driver.days.map((day) => <DayTable key={day.key} day={day} title={titles.get(day.key)} />)}</div> : null}
    </td></tr>
  </>;
}

function ServiceDetail({ model }) {
  const [byDriver, setByDriver] = React.useState(false);
  const { time } = model;
  const titles = React.useMemo(() => dayTitles(time.days), [time.days]);
  return <section aria-labelledby="client-explanation-detail" className="billing-client-explanation-summary">
    <div className="billing-client-explanation-detail-title">
      <h2 id="client-explanation-detail">Détail</h2>
      <span role="group" aria-label="Regrouper le détail" className="billing-client-explanation-switch">
        <button type="button" aria-pressed={!byDriver} onClick={() => setByDriver(false)}>Par jour</button>
        <button type="button" aria-pressed={byDriver} onClick={() => setByDriver(true)}>Par chauffeur</button>
      </span>
    </div>
    {byDriver ? <div className="billing-client-explanation-table-wrap">
      <table className="billing-client-explanation-drivers" aria-label="Détail par chauffeur">
        <thead><tr><th scope="col">Chauffeur · camion</th><th scope="col">Journées</th><th scope="col">Route</th><th scope="col">Sur place</th><th scope="col">Inspection et retour</th><th scope="col">Heures</th><th scope="col">Montant</th></tr></thead>
        <tbody>{model.drivers.map((driver) => <DriverRows key={driver.key} driver={driver} titles={titles} />)}</tbody>
        <tfoot><tr><th scope="row">Total · {plural(model.drivers.length, 'chauffeur')}</th><td>{workedDayCount(time.days)}</td><td>{road(time, 0)}</td><td>{onsite(time)}</td>
          <td>{timeLabel(time.inspection + time.back)}</td><td>{hoursLabel(time.hours)}</td><td>{moneyLabel(time.totals)}</td></tr></tfoot>
      </table>
    </div> : <div className="billing-client-explanation-days">{time.days.map((day) => <DayTable key={day.key} day={day} title={titles.get(day.key)} />)}</div>}
    <p className="billing-client-explanation-footnote">Durées arrondies à la minute. Inspection et retour : seule votre part du temps commun du camion est comptée.</p>
  </section>;
}

// statement : relevé de transport expliqué ; son total (surtaxe comprise) remplace celui des prestations.
export default function BillingClientExplanationView({ model, statement = null }) {
  const [expandedKey, setExpandedKey] = React.useState(null);
  if (!model.itemCount) return <div className="billing-client-explanation-state"><h2>Aucune prestation à présenter</h2><p>Revenez à la facturation pour sélectionner une entreprise.</p></div>;
  const statementTotal = statement ? statementTotalSummary(statement) : null;
  return <main className="billing-client-explanation-page">
    <header className="billing-client-explanation-header">
      <div>
        <h1>{model.clientName}</h1>
        {!model.accountConfirmed ? <p className="billing-client-explanation-account-note">Entreprise facturée à confirmer</p> : null}
        <p className="billing-client-explanation-period"><Period model={model} /></p>
      </div>
      {statementTotal ? <div className="billing-client-explanation-total" aria-label={statementTotal.label}>
        <p>{statementTotal.label}</p><strong>{statementTotal.total}</strong>
        {statementTotal.detail ? <p className="billing-client-explanation-total-detail">{statementTotal.detail}</p> : null}
      </div> : <>
        <div className="billing-client-explanation-total" aria-label="Total avant taxes des prestations présentées">
          <p>Total avant taxes</p><strong><MoneyTotals totals={model.totals} /></strong>
        </div>
        <p className="billing-client-explanation-scope">Ce montant correspond aux prestations présentées, avant taxes. Cette présentation ne constitue pas une facture finale.</p>
      </>}
    </header>

    {model.time.hours !== undefined ? <HoursSummary time={model.time} /> : null}

    {model.extraCategories.length || model.freeLines.length ? <section aria-labelledby="client-explanation-extras" className="billing-client-explanation-summary">
      <h2 id="client-explanation-extras">Autres frais</h2>
      <div className="billing-client-explanation-table-wrap">
        <table className="billing-client-explanation-table" aria-label="Autres frais">
          <thead><tr><th scope="col">Prestation</th><th scope="col">Quantité</th><th scope="col">Tarif</th><th scope="col">Montant</th><th scope="col"><span className="sr-only">Détail</span></th></tr></thead>
          <tbody>{model.extraCategories.map((category) => <CategorySummaryRow key={category.key} category={category}
            expanded={expandedKey === category.key} onToggle={() => setExpandedKey(expandedKey === category.key ? null : category.key)} />)}
            {model.freeLines.map((line) => <tr key={line.key} className="billing-client-explanation-category is-static">
              <th scope="row" className="billing-client-explanation-category-name"><span>{line.label}</span></th>
              <td colSpan={2} />
              <td className="billing-client-explanation-amount" data-label="Montant">{formatBillingMoney(line.amount, line.currency)}</td>
              <td />
            </tr>)}</tbody>
        </table>
      </div>
    </section> : null}

    {model.time.hours !== undefined ? <ServiceDetail model={model} /> : null}

    {model.notes.length ? <section aria-labelledby="client-explanation-notes" className="billing-client-explanation-notes">
      <h2 id="client-explanation-notes">Informations à confirmer</h2>
      <ul>{model.notes.map((note) => <li key={note}>{note}</li>)}</ul>
    </section> : null}

    <details className="billing-client-explanation-method">
      <summary><span>Comment le montant est calculé</span><ChevronDown size={16} aria-hidden="true" /></summary>
      <div>
        <p>Chaque prestation reprend la quantité attribuée, son tarif et le montant enregistré. Le total additionne ces montants, arrondis individuellement au centime. Les durées et les parts affichées peuvent être arrondies pour faciliter la lecture.</p>
        <p>Lorsqu’un temps est partagé, seule la part attribuée est comptée. Les durées de trajet et de service relevées expliquent cette répartition et ne sont pas ajoutées une seconde fois.</p>
        <p>Les temps exclus sont indiqués lorsqu’ils sont connus. Une information absente ne signifie pas que la prestation est gratuite ou incluse. Les taxes et les autres éléments d’une facture finale ne sont pas compris dans ce total.</p>
      </div>
    </details>
  </main>;
}
